import { useEffect, useMemo } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import FormField from '@/components/ui/FormField'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/Select'
import { Checkbox } from '@/components/ui/Checkbox'
import { Button } from '@/components/ui/button'

const numericField = z
  .string()
  .trim()
  .optional()
  .refine((value) => !value || /^[0-9]+(\.[0-9]+)?$/.test(value), 'Enter a valid number')

const paymentAccountSchema = z.object({
  id: z.string().optional(),
  bankName: z.string().min(1, 'Bank name is required'),
  bankCode: z.string().optional(),
  accountName: z.string().min(1, 'Account name is required'),
  accountNumber: z.string().min(1, 'Account number is required'),
  currency: z.string().min(1, 'Currency is required'),
  instructions: z.string().optional(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
})

const formSchema = z
  .object({
    title: z.string().min(1, 'Cohort name is required'),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    examDate: z.string().optional(),
    firstYrFirstSemesterStartDate: z.string().optional(),
    firstYrFirstSemesterEndDate: z.string().optional(),
    firstYrSecondSemesterStartDate: z.string().optional(),
    firstYrSecondSemesterEndDate: z.string().optional(),
    secondYrFirstSemesterStartDate: z.string().optional(),
    secondYrFirstSemesterEndDate: z.string().optional(),
    secondYrSecondSemesterStartDate: z.string().optional(),
    secondYrSecondSemesterEndDate: z.string().optional(),
    firstYrFirstSemesterExamDate: z.string().optional(),
    firstYrSecondSemesterExamDate: z.string().optional(),
    secondYrFirstSemesterExamDate: z.string().optional(),
    secondYrSecondSemesterExamDate: z.string().optional(),
    capacity: numericField,
    fee: numericField,
    currency: z.string().min(1, 'Currency is required'),
    isActive: z.boolean().default(true),
    paymentAccounts: z.array(paymentAccountSchema).optional(),
  })
  .refine(
    (values) => {
      if (!values.startDate || !values.endDate) {
        return true
      }
      return new Date(values.endDate) >= new Date(values.startDate)
    },
    {
      message: 'End date cannot be earlier than start date',
      path: ['endDate'],
    },
  )
  .refine(
    (values) => {
      if (!values.firstYrFirstSemesterStartDate || !values.firstYrFirstSemesterEndDate) return true
      return new Date(values.firstYrFirstSemesterEndDate) >= new Date(values.firstYrFirstSemesterStartDate)
    },
    {
      message: 'Year 1 Semester 1 end date cannot be earlier than start date',
      path: ['firstYrFirstSemesterEndDate'],
    },
  )
  .refine(
    (values) => {
      if (!values.firstYrSecondSemesterStartDate || !values.firstYrSecondSemesterEndDate) return true
      return new Date(values.firstYrSecondSemesterEndDate) >= new Date(values.firstYrSecondSemesterStartDate)
    },
    {
      message: 'Year 1 Semester 2 end date cannot be earlier than start date',
      path: ['firstYrSecondSemesterEndDate'],
    },
  )
  .refine(
    (values) => {
      if (!values.secondYrFirstSemesterStartDate || !values.secondYrFirstSemesterEndDate) return true
      return new Date(values.secondYrFirstSemesterEndDate) >= new Date(values.secondYrFirstSemesterStartDate)
    },
    {
      message: 'Year 2 Semester 1 end date cannot be earlier than start date',
      path: ['secondYrFirstSemesterEndDate'],
    },
  )
  .refine(
    (values) => {
      if (!values.secondYrSecondSemesterStartDate || !values.secondYrSecondSemesterEndDate) return true
      return new Date(values.secondYrSecondSemesterEndDate) >= new Date(values.secondYrSecondSemesterStartDate)
    },
    {
      message: 'Year 2 Semester 2 end date cannot be earlier than start date',
      path: ['secondYrSecondSemesterEndDate'],
    },
  )

export type ProgrammeCohortFormValues = z.infer<typeof formSchema>

const defaultCurrencies = ['NGN', 'USD', 'GBP', 'EUR']

const defaultPaymentAccount = {
  bankName: '',
  bankCode: '',
  accountName: '',
  accountNumber: '',
  currency: 'NGN',
  instructions: '',
  isDefault: true,
  isActive: true,
}

export interface ProgrammeCohortFormProps {
  mode: 'create' | 'edit'
  defaultValues?: Partial<ProgrammeCohortFormValues>
  submitting?: boolean
  onSubmit: (values: ProgrammeCohortFormValues) => Promise<void> | void
  onCancel?: () => void
  currencyOptions?: string[]
  initialPaymentAccounts?: any[]
}

export function ProgrammeCohortForm({
  mode,
  defaultValues,
  submitting = false,
  onSubmit,
  onCancel,
  currencyOptions = defaultCurrencies,
  initialPaymentAccounts,
}: ProgrammeCohortFormProps) {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm<ProgrammeCohortFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: defaultValues?.title ?? '',
      startDate: defaultValues?.startDate ?? undefined,
      endDate: defaultValues?.endDate ?? undefined,
      examDate: defaultValues?.examDate ?? undefined,
      firstYrFirstSemesterStartDate: defaultValues?.firstYrFirstSemesterStartDate ?? undefined,
      firstYrFirstSemesterEndDate: defaultValues?.firstYrFirstSemesterEndDate ?? undefined,
      firstYrSecondSemesterStartDate: defaultValues?.firstYrSecondSemesterStartDate ?? undefined,
      firstYrSecondSemesterEndDate: defaultValues?.firstYrSecondSemesterEndDate ?? undefined,
      secondYrFirstSemesterStartDate: defaultValues?.secondYrFirstSemesterStartDate ?? undefined,
      secondYrFirstSemesterEndDate: defaultValues?.secondYrFirstSemesterEndDate ?? undefined,
      secondYrSecondSemesterStartDate: defaultValues?.secondYrSecondSemesterStartDate ?? undefined,
      secondYrSecondSemesterEndDate: defaultValues?.secondYrSecondSemesterEndDate ?? undefined,
      firstYrFirstSemesterExamDate: defaultValues?.firstYrFirstSemesterExamDate ?? undefined,
      firstYrSecondSemesterExamDate: defaultValues?.firstYrSecondSemesterExamDate ?? undefined,
      secondYrFirstSemesterExamDate: defaultValues?.secondYrFirstSemesterExamDate ?? undefined,
      secondYrSecondSemesterExamDate: defaultValues?.secondYrSecondSemesterExamDate ?? undefined,
      capacity: defaultValues?.capacity ?? '',
      fee: defaultValues?.fee ?? '',
      currency: defaultValues?.currency ?? currencyOptions[0] ?? 'NGN',
      isActive: defaultValues?.isActive ?? true,
      paymentAccounts: initialPaymentAccounts ?? (defaultValues as any)?.paymentAccounts ?? [],
    },
  })

  const {
    fields: paymentAccountFields,
    append: appendPaymentAccount,
    remove: removePaymentAccount,
  } = useFieldArray({
    control,
    name: 'paymentAccounts',
  })

  const watchedPaymentAccounts = watch('paymentAccounts') ?? []

  const handleAddAccount = () => {
    appendPaymentAccount({
      ...defaultPaymentAccount,
      currency: currencyOptions[0] ?? 'NGN',
      isDefault: watchedPaymentAccounts.length === 0,
    })
  }

  const markDefaultAccount = (index: number) => {
    watchedPaymentAccounts.forEach((_, idx) => {
      setValue(`paymentAccounts.${idx}.isDefault`, idx === index, {
        shouldValidate: true,
        shouldDirty: true,
      })
    })
  }

  useEffect(() => {
    reset({
      title: defaultValues?.title ?? '',
      startDate: defaultValues?.startDate ?? undefined,
      endDate: defaultValues?.endDate ?? undefined,
      examDate: defaultValues?.examDate ?? undefined,
      firstYrFirstSemesterStartDate: defaultValues?.firstYrFirstSemesterStartDate ?? undefined,
      firstYrFirstSemesterEndDate: defaultValues?.firstYrFirstSemesterEndDate ?? undefined,
      firstYrSecondSemesterStartDate: defaultValues?.firstYrSecondSemesterStartDate ?? undefined,
      firstYrSecondSemesterEndDate: defaultValues?.firstYrSecondSemesterEndDate ?? undefined,
      secondYrFirstSemesterStartDate: defaultValues?.secondYrFirstSemesterStartDate ?? undefined,
      secondYrFirstSemesterEndDate: defaultValues?.secondYrFirstSemesterEndDate ?? undefined,
      secondYrSecondSemesterStartDate: defaultValues?.secondYrSecondSemesterStartDate ?? undefined,
      secondYrSecondSemesterEndDate: defaultValues?.secondYrSecondSemesterEndDate ?? undefined,
      firstYrFirstSemesterExamDate: defaultValues?.firstYrFirstSemesterExamDate ?? undefined,
      firstYrSecondSemesterExamDate: defaultValues?.firstYrSecondSemesterExamDate ?? undefined,
      secondYrFirstSemesterExamDate: defaultValues?.secondYrFirstSemesterExamDate ?? undefined,
      secondYrSecondSemesterExamDate: defaultValues?.secondYrSecondSemesterExamDate ?? undefined,
      capacity: defaultValues?.capacity ?? '',
      fee: defaultValues?.fee ?? '',
      currency: defaultValues?.currency ?? currencyOptions[0] ?? 'NGN',
      isActive: defaultValues?.isActive ?? true,
      paymentAccounts: initialPaymentAccounts ?? (defaultValues as any)?.paymentAccounts ?? [],
    })
  }, [defaultValues, reset, currencyOptions, initialPaymentAccounts])

  const handleFormSubmit = (values: ProgrammeCohortFormValues) => {
    onSubmit(values)
  }

  return (
    <form className="space-y-5" onSubmit={handleSubmit(handleFormSubmit)}>
      <FormField label="Cohort name" required>
        <Input
          placeholder="e.g. MBA Full Time - Sept 2025"
          {...register('title')}
          className={errors.title ? 'border-red-500 focus:ring-red-200 focus:border-red-500' : ''}
        />
        {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title.message}</p>}
      </FormField>

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label="Start date">
          <Input
            type="date"
            {...register('startDate')}
            className={errors.startDate ? 'border-red-500 focus:ring-red-200 focus:border-red-500' : ''}
          />
          {errors.startDate && (
            <p className="text-xs text-red-500 mt-1">{errors.startDate.message}</p>
          )}
        </FormField>
        <FormField label="End date">
          <Input
            type="date"
            {...register('endDate')}
            className={errors.endDate ? 'border-red-500 focus:ring-red-200 focus:border-red-500' : ''}
          />
          {errors.endDate && <p className="text-xs text-red-500 mt-1">{errors.endDate.message}</p>}
        </FormField>
      </div>

      <div className="rounded-lg border border-secondary-200 p-4 space-y-3 bg-secondary-50/50">
        <p className="text-sm font-medium text-secondary-800">Semester timelines (optional)</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="Year 1 • First semester start">
            <Input
              type="date"
              {...register('firstYrFirstSemesterStartDate')}
              className={errors.firstYrFirstSemesterStartDate ? 'border-red-500 focus:ring-red-200 focus:border-red-500' : ''}
            />
            {errors.firstYrFirstSemesterStartDate && (
              <p className="text-xs text-red-500 mt-1">{errors.firstYrFirstSemesterStartDate.message}</p>
            )}
          </FormField>
          <FormField label="Year 1 • First semester end">
            <Input
              type="date"
              {...register('firstYrFirstSemesterEndDate')}
              className={errors.firstYrFirstSemesterEndDate ? 'border-red-500 focus:ring-red-200 focus:border-red-500' : ''}
            />
            {errors.firstYrFirstSemesterEndDate && (
              <p className="text-xs text-red-500 mt-1">{errors.firstYrFirstSemesterEndDate.message}</p>
            )}
          </FormField>
          <FormField label="Year 1 • Second semester start">
            <Input
              type="date"
              {...register('firstYrSecondSemesterStartDate')}
              className={errors.firstYrSecondSemesterStartDate ? 'border-red-500 focus:ring-red-200 focus:border-red-500' : ''}
            />
            {errors.firstYrSecondSemesterStartDate && (
              <p className="text-xs text-red-500 mt-1">{errors.firstYrSecondSemesterStartDate.message}</p>
            )}
          </FormField>
          <FormField label="Year 1 • Second semester end">
            <Input
              type="date"
              {...register('firstYrSecondSemesterEndDate')}
              className={errors.firstYrSecondSemesterEndDate ? 'border-red-500 focus:ring-red-200 focus:border-red-500' : ''}
            />
            {errors.firstYrSecondSemesterEndDate && (
              <p className="text-xs text-red-500 mt-1">{errors.firstYrSecondSemesterEndDate.message}</p>
            )}
          </FormField>
          <FormField label="Year 2 • First semester start">
            <Input
              type="date"
              {...register('secondYrFirstSemesterStartDate')}
              className={errors.secondYrFirstSemesterStartDate ? 'border-red-500 focus:ring-red-200 focus:border-red-500' : ''}
            />
            {errors.secondYrFirstSemesterStartDate && (
              <p className="text-xs text-red-500 mt-1">{errors.secondYrFirstSemesterStartDate.message}</p>
            )}
          </FormField>
          <FormField label="Year 2 • First semester end">
            <Input
              type="date"
              {...register('secondYrFirstSemesterEndDate')}
              className={errors.secondYrFirstSemesterEndDate ? 'border-red-500 focus:ring-red-200 focus:border-red-500' : ''}
            />
            {errors.secondYrFirstSemesterEndDate && (
              <p className="text-xs text-red-500 mt-1">{errors.secondYrFirstSemesterEndDate.message}</p>
            )}
          </FormField>
          <FormField label="Year 2 • Second semester start">
            <Input
              type="date"
              {...register('secondYrSecondSemesterStartDate')}
              className={errors.secondYrSecondSemesterStartDate ? 'border-red-500 focus:ring-red-200 focus:border-red-500' : ''}
            />
            {errors.secondYrSecondSemesterStartDate && (
              <p className="text-xs text-red-500 mt-1">{errors.secondYrSecondSemesterStartDate.message}</p>
            )}
          </FormField>
          <FormField label="Year 2 • Second semester end">
            <Input
              type="date"
              {...register('secondYrSecondSemesterEndDate')}
              className={errors.secondYrSecondSemesterEndDate ? 'border-red-500 focus:ring-red-200 focus:border-red-500' : ''}
            />
            {errors.secondYrSecondSemesterEndDate && (
              <p className="text-xs text-red-500 mt-1">{errors.secondYrSecondSemesterEndDate.message}</p>
            )}
          </FormField>
        </div>
      </div>

      <div className="rounded-lg border border-secondary-200 p-4 space-y-3 bg-secondary-50/50">
        <p className="text-sm font-medium text-secondary-800">Exam dates (optional)</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="Programme exam date">
            <Input
              type="date"
              {...register('examDate')}
              className={errors.examDate ? 'border-red-500 focus:ring-red-200 focus:border-red-500' : ''}
            />
            {errors.examDate && (
              <p className="text-xs text-red-500 mt-1">{errors.examDate.message}</p>
            )}
          </FormField>
          <div />
          <FormField label="Year 1 • First semester exam">
            <Input
              type="date"
              {...register('firstYrFirstSemesterExamDate')}
              className={
                errors.firstYrFirstSemesterExamDate
                  ? 'border-red-500 focus:ring-red-200 focus:border-red-500'
                  : ''
              }
            />
            {errors.firstYrFirstSemesterExamDate && (
              <p className="text-xs text-red-500 mt-1">{errors.firstYrFirstSemesterExamDate.message}</p>
            )}
          </FormField>
          <FormField label="Year 1 • Second semester exam">
            <Input
              type="date"
              {...register('firstYrSecondSemesterExamDate')}
              className={
                errors.firstYrSecondSemesterExamDate
                  ? 'border-red-500 focus:ring-red-200 focus:border-red-500'
                  : ''
              }
            />
            {errors.firstYrSecondSemesterExamDate && (
              <p className="text-xs text-red-500 mt-1">{errors.firstYrSecondSemesterExamDate.message}</p>
            )}
          </FormField>
          <FormField label="Year 2 • First semester exam">
            <Input
              type="date"
              {...register('secondYrFirstSemesterExamDate')}
              className={
                errors.secondYrFirstSemesterExamDate
                  ? 'border-red-500 focus:ring-red-200 focus:border-red-500'
                  : ''
              }
            />
            {errors.secondYrFirstSemesterExamDate && (
              <p className="text-xs text-red-500 mt-1">{errors.secondYrFirstSemesterExamDate.message}</p>
            )}
          </FormField>
          <FormField label="Year 2 • Second semester exam">
            <Input
              type="date"
              {...register('secondYrSecondSemesterExamDate')}
              className={
                errors.secondYrSecondSemesterExamDate
                  ? 'border-red-500 focus:ring-red-200 focus:border-red-500'
                  : ''
              }
            />
            {errors.secondYrSecondSemesterExamDate && (
              <p className="text-xs text-red-500 mt-1">{errors.secondYrSecondSemesterExamDate.message}</p>
            )}
          </FormField>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label="Capacity (learners)">
          <Input
            type="number"
            min={0}
            placeholder="e.g. 150"
            {...register('capacity')}
            className={errors.capacity ? 'border-red-500 focus:ring-red-200 focus:border-red-500' : ''}
          />
          {errors.capacity && (
            <p className="text-xs text-red-500 mt-1">{errors.capacity.message}</p>
          )}
        </FormField>
        <FormField label="Tuition fee">
          <Input
            type="number"
            min={0}
            step="0.01"
            placeholder="e.g. 250000"
            {...register('fee')}
            className={errors.fee ? 'border-red-500 focus:ring-red-200 focus:border-red-500' : ''}
          />
          {errors.fee && <p className="text-xs text-red-500 mt-1">{errors.fee.message}</p>}
        </FormField>
      </div>

      <FormField label="Currency">
        <Select
          {...register('currency')}
          className={errors.currency ? 'border-red-500 focus:ring-red-200 focus:border-red-500' : ''}
        >
          {currencyOptions.map((currency) => (
            <option key={currency} value={currency}>
              {currency}
            </option>
          ))}
        </Select>
        {errors.currency && (
          <p className="text-xs text-red-500 mt-1">{errors.currency.message}</p>
        )}
      </FormField>

      <div className="pt-1">
        <Checkbox label="Cohort is active and visible to learners" {...register('isActive')} />
      </div>

      <section className="space-y-4 rounded-lg border border-secondary-200 p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-secondary-900">Payment Accounts</h3>
          <Button type="button" variant="outline" size="sm" onClick={handleAddAccount}>
            Add Account
          </Button>
        </div>

        {paymentAccountFields.length === 0 && (
          <p className="text-sm text-secondary-500 italic">No payment accounts added yet.</p>
        )}

        {paymentAccountFields.map((field, index) => (
          <div key={field.id} className="rounded-lg border border-secondary-200 p-4 space-y-4 bg-white">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-secondary-700">Account {index + 1}</h4>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                onClick={() => removePaymentAccount(index)}
              >
                Remove
              </Button>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <FormField label="Bank Name" required>
                <Input
                  placeholder="e.g. GTBank"
                  {...register(`paymentAccounts.${index}.bankName` as const)}
                  className={errors.paymentAccounts?.[index]?.bankName ? 'border-red-500' : ''}
                />
              </FormField>

              <FormField label="Bank Code">
                <Input
                  placeholder="e.g. 058"
                  {...register(`paymentAccounts.${index}.bankCode` as const)}
                />
              </FormField>

              <FormField label="Account Name" required>
                <Input
                  placeholder="e.g. KBS Ltd"
                  {...register(`paymentAccounts.${index}.accountName` as const)}
                  className={errors.paymentAccounts?.[index]?.accountName ? 'border-red-500' : ''}
                />
              </FormField>

              <FormField label="Account Number" required>
                <Input
                  placeholder="e.g. 0123456789"
                  {...register(`paymentAccounts.${index}.accountNumber` as const)}
                  className={errors.paymentAccounts?.[index]?.accountNumber ? 'border-red-500' : ''}
                />
              </FormField>

              <FormField label="Currency" required>
                <Input
                  placeholder="NGN"
                  {...register(`paymentAccounts.${index}.currency` as const)}
                  className={errors.paymentAccounts?.[index]?.currency ? 'border-red-500' : ''}
                />
              </FormField>

              <div className="md:col-span-2">
                <FormField label="Payment Instructions">
                  <textarea
                    rows={2}
                    className="w-full rounded-md border border-secondary-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7b2a40]/40"
                    placeholder="Provide instructions for applicants making payments."
                    {...register(`paymentAccounts.${index}.instructions` as const)}
                  />
                </FormField>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 pt-2">
              <Checkbox
                label="Set as default"
                checked={watchedPaymentAccounts[index]?.isDefault ?? false}
                onChange={() => markDefaultAccount(index)}
              />
              <Checkbox
                label="Account is active"
                checked={watchedPaymentAccounts[index]?.isActive ?? true}
                onChange={(e) =>
                  setValue(`paymentAccounts.${index}.isActive`, e.target.checked, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
              />
            </div>
          </div>
        ))}
      </section>

      <div className="flex justify-end gap-2 pt-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : mode === 'create' ? 'Create Cohort' : 'Save Changes'}
        </Button>
      </div>
    </form>
  )
}
